import React from 'react';
import { TableRow, TableCell, useTheme, useMediaQuery, Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { OptionCell, LTPCell } from './OptionTableCell';
import StrikeCell from './StrikeCell';

// Regular styled table row
const StyledTableRow = styled(TableRow)(({ theme, isAtmRow }) => ({
  height: '42px',
  borderBottom: `1px solid ${theme.palette.divider}`,
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.02)'
      : 'rgba(0, 0, 0, 0.02)'
  }
}));

// LTP line row that displays the spot price
const LtpLineRow = styled(TableRow)(({ theme }) => ({
  height: 0,
  padding: 0,
  margin: '-1px 0 0 0',
  border: 'none',
  position: 'relative',
  borderBottom: 'none',
  borderTop: 'none'
}));

// Line cell
const LineCell = styled(TableCell)(({ theme }) => ({
  position: 'relative',
  padding: 0,
  margin: 0,
  height: 0,
  border: 'none',
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    backgroundColor: theme.palette.primary.main,
    zIndex: 0
  }
}));

// Spot price value display
const SpotPriceValue = styled(Typography)(({ theme }) => ({
  display: 'inline-block',
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.success.main,
  fontSize: '0.75rem',
  fontWeight: 600,
  padding: '2px 8px',
  borderRadius: '10px',
  border: `1px solid ${theme.palette.primary.main}`,
  position: 'absolute',
  left: '50%',
  top: '-9px',
  transform: 'translateX(-50%)',
  zIndex: 1,
  [theme.breakpoints.down('sm')]: {
    fontSize: '0.7rem',
    padding: '1px 6px'
  }
}));

const OptionRow = ({ 
  row, 
  index, 
  atmIndex, 
  spotPrice, 
  callHeaders, 
  putHeaders, 
  clickedCells, 
  handleLTPClick 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Calculate if this is the ATM row
  const isAtmRow = index === atmIndex;
  const strikePrice = parseFloat(row.strike_price);
  
  // Format the spot price with 2 decimal places
  const formattedSpotPrice = spotPrice ? spotPrice.toFixed(2) : '0.00';
  
  // Only show spot price line if this is the ATM row determined by atmIndex
  const showSpotPriceLine = isAtmRow && spotPrice && spotPrice > 0;
  
  // Filter headers for mobile view
  const filteredCallHeaders = isMobile 
    ? callHeaders.filter(header => ['ltp'].includes(header.toLowerCase()))
    : callHeaders;  // Show all columns on desktop
  
  const filteredPutHeaders = isMobile 
    ? putHeaders.filter(header => ['ltp'].includes(header.toLowerCase()))
    : putHeaders;  // Show all columns on desktop
  
  // Calculate percentage change values
  const ceLTP = parseFloat(row.ce_ltp) || 0;
  const ceClose = parseFloat(row.ce_close) || 0;
  const peLTP = parseFloat(row.pe_ltp) || 0;
  const peClose = parseFloat(row.pe_close) || 0;
  
  const cePercentChange = ceClose !== 0 ? ((ceLTP - ceClose) / ceClose) * 100 : 0;
  const pePercentChange = peClose !== 0 ? ((peLTP - peClose) / peClose) * 100 : 0;
  
  return (
    <>
      <StyledTableRow isAtmRow={isAtmRow}>
        {/* Call Option Cells */}
        {filteredCallHeaders.map((header, i) => {
          const headerLower = header.toLowerCase();
          let value = row[`ce_${headerLower}`];
          
          const isLTPColumn = headerLower === 'ltp';
          const numValue = parseFloat(value);
          const valueClass = numValue > 0
            ? 'value-up'
            : numValue < 0
              ? 'value-down'
              : '';
          const cellId = `${row.strike_price}-ce`;
          const isClicked = clickedCells[cellId];

          return isLTPColumn ? (
            <LTPCell
              key={`call-${i}`}
              header={header}
              value={value}
              isAtmRow={isAtmRow}
              valueClass={valueClass}
              side="ce"
              onClick={(e) => handleLTPClick(row, 'ce', e)}
              percentChange={cePercentChange}
            />
          ) : (
            <OptionCell
              key={`call-${i}`}
              header={header}
              value={value}
              isAtmRow={isAtmRow}
              valueClass={valueClass}
            />
          );
        })}
        
        {/* Strike Price Cell */}
        <StrikeCell 
          strikePrice={row.strike_price} 
          spotPrice={spotPrice} 
          isAtmRow={isAtmRow} 
        />
        
        {/* Put Option Cells */}
        {filteredPutHeaders.map((header, i) => {
          const headerLower = header.toLowerCase();
          let value = row[`pe_${headerLower}`];
          
          const isLTPColumn = headerLower === 'ltp';
          const numValue = parseFloat(value);
          const valueClass = numValue > 0
            ? 'value-up'
            : numValue < 0
              ? 'value-down'
              : '';
          const cellId = `${row.strike_price}-pe`;
          const isClicked = clickedCells[cellId];

          return isLTPColumn ? (
            <LTPCell
              key={`put-${i}`}
              header={header}
              value={value}
              isAtmRow={isAtmRow}
              valueClass={valueClass}
              side="pe"
              onClick={(e) => handleLTPClick(row, 'pe', e)}
              percentChange={pePercentChange}
            />
          ) : (
            <OptionCell
              key={`put-${i}`}
              header={header}
              value={value}
              isAtmRow={isAtmRow}
              valueClass={valueClass}
            />
          );
        })}
      </StyledTableRow>

      {/* Display spot price line after ATM row */}
      {showSpotPriceLine && (
        <LtpLineRow>
          <LineCell colSpan={filteredCallHeaders.length + 1 + filteredPutHeaders.length}>
            <SpotPriceValue>
              {formattedSpotPrice}
            </SpotPriceValue>
          </LineCell>
        </LtpLineRow>
      )}
    </>
  );
};

export default OptionRow; 